import { useState, useCallback } from 'react';
import reservationService from '../services/reservationService';
import { getCurrentYear } from '../utils/eventStatus';

export const useReservationManagement = () => {
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    currentPage: 1,
    lastPage: 0,
    from: 0,
    to: 0
  });
  const [filters, setFilters] = useState({
    eventId: '',
    studentId: '',
    accessCode: '',
    status: 'all',
    year: getCurrentYear()
  });

  // Function to apply filters
  const applyFilters = useCallback((reservationList, currentFilters) => {
    return reservationList.filter(reservation => {
      // Filtro por código de acceso
      if (currentFilters.accessCode && !reservation.accessCode.toLowerCase().includes(currentFilters.accessCode.toLowerCase())) {
        return false;
      }
      // Filtro por nombre del Alumno
      if (currentFilters.studentId && !reservation.studentName.toLowerCase().includes(currentFilters.studentId.toLowerCase())) {
        return false;
      }
      // Filtro por ID del evento
      if (currentFilters.eventId && reservation.eventId !== currentFilters.eventId) {
        return false;
      }
      // Filtro por estado
      if (currentFilters.status !== 'all' && reservation.overallStatus !== currentFilters.status) {
        return false;
      }
      return true;
    });
  }, []);

  const fetchGroupedReservations = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      // Get current page and limit from params or fallback to pagination state
      const currentPage = params.page ?? pagination.page;
      const currentLimit = params.limit ?? pagination.limit;
      
      const response = await reservationService.getGroupedReservations({
        ...filters,
        ...params,
        page: currentPage,
        limit: currentLimit
      });
      
      setReservations(response.data);
      setFilteredReservations(response.data);
      
      // Transform API pagination to match DataTable expected format
      const transformedPagination = {
        currentPage: response.pagination.page,
        lastPage: response.pagination.totalPages,
        limit: response.pagination.limit,
        total: response.pagination.total,
        from: ((response.pagination.page - 1) * response.pagination.limit) + 1,
        to: Math.min(response.pagination.page * response.pagination.limit, response.pagination.total),
        page: response.pagination.page,
        totalPages: response.pagination.totalPages
      };
      
      setPagination(transformedPagination);
      return response;
    } catch (err) {
      setError(err.message);
      setReservations([]);
      setFilteredReservations([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  const fetchAllReservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reservationService.getAllReservations();
      setReservations(data);
      setFilteredReservations(applyFilters(data, filters));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, applyFilters]);

  const searchReservations = useCallback(async (searchParams) => {
    setLoading(true);
    setError(null);
    try {
      const response = await reservationService.getGroupedReservations(searchParams);
      setReservations(response.data);
      setFilteredReservations(response.data);
      setPagination(response.pagination);
      return response;
    } catch (err) {
      setError(err.message);
      setReservations([]);
      setFilteredReservations([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar filtros (solo actualiza estado, no aplica)
  const applyReservationFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    // Solo aplicar filtros si se está aplicando explícitamente
    if (newFilters.applyNow) {
      const { applyNow, ...filtersToApply } = newFilters;
      setFilteredReservations(applyFilters(reservations, filtersToApply));
    }
  }, [reservations, applyFilters]);

  // Aplicar filtros explícitamente (cuando se presiona el botón)
  const applyFiltersNow = useCallback(() => {
    setFilteredReservations(applyFilters(reservations, filters));
  }, [reservations, filters, applyFilters]);

  const clearFilters = useCallback(() => {
    const defaultFilters = { eventId: '', studentId: '', accessCode: '', status: 'all', year: getCurrentYear() };
    setFilters(defaultFilters);
    setFilteredReservations(reservations);
  }, [reservations]);

  const markAsSold = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservation = await reservationService.markAsSold(id);
      const updatedReservations = reservations.map(reservation => 
        reservation.id === id ? { ...reservation, overallStatus: 'sold' } : reservation
      );
      setReservations(updatedReservations);
      setFilteredReservations(applyFilters(updatedReservations, filters));
      
      return updatedReservation;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const markAsDelivered = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservation = await reservationService.markAsDelivered(id);
      const updatedReservations = reservations.map(reservation => 
        reservation.id === id ? { ...reservation, overallStatus: 'delivered' } : reservation
      );
      setReservations(updatedReservations);
      setFilteredReservations(applyFilters(updatedReservations, filters));
      
      return updatedReservation;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const markGroupAsSold = useCallback(async (studentId, accessCodeId) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservations = await reservationService.markGroupAsSold(studentId, accessCodeId);
      const updatedReservationsList = reservations.map(reservation => 
        reservation.studentId === studentId && reservation.accessCodeId === accessCodeId 
          ? { ...reservation, overallStatus: 'sold' } 
          : reservation
      );
      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));
      
      return updatedReservations;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const markGroupAsDelivered = useCallback(async (studentId, accessCodeId) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservations = await reservationService.markGroupAsDelivered(studentId, accessCodeId);
      const updatedReservationsList = reservations.map(reservation => 
        reservation.studentId === studentId && reservation.accessCodeId === accessCodeId 
          ? { ...reservation, overallStatus: 'delivered' } 
          : reservation
      );
      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));
      
      return updatedReservations;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const returnReservation = useCallback(async (id, reason = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservation = await reservationService.returnReservation(id, reason, true); // fromAdmin = true
      const updatedReservationsList = reservations.map(reservation => {
        if (reservation.seats && reservation.seats.some(seat => seat.id === id)) {
          // Actualizar el estado de la reserva específica en el grupo
          return {
            ...reservation,
            seats: reservation.seats.map(seat => 
              seat.id === id ? { ...seat, status: 'returned' } : seat
            ),
            overallStatus: reservation.seats.every(seat => 
              seat.id === id ? true : seat.status === 'returned'
            ) ? 'returned' : reservation.overallStatus
          };
        }
        return reservation;
      });
      
      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));
      
      return updatedReservation;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const markAsNoCost = useCallback(async (id, reason) => {
    setLoading(true);
    setError(null);

    try {
      const updatedReservation = await reservationService.markAsNoCost(id, reason);
      const updatedReservationsList = reservations.map(reservation => {
        if (reservation.seats && reservation.seats.some(seat => seat.id === id)) {
          return {
            ...reservation,
            seats: reservation.seats.map(seat =>
              seat.id === id
                ? { ...seat, price: 0, isFreeOfCharge: true, status: updatedReservation?.status ?? seat.status }
                : seat
            ),
          };
        }
        return reservation;
      });

      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));

      return updatedReservation;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const returnGroupReservations = useCallback(async (accessCodeId, reason = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservations = await reservationService.returnGroupReservations(accessCodeId, reason, true); // fromAdmin = true
      const updatedReservationsList = reservations.map(reservation => 
        reservation.accessCodeId === accessCodeId 
          ? { ...reservation, overallStatus: 'returned' } 
          : reservation
      );
      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));
      
      return updatedReservations;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const cancelReservation = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservation = await reservationService.cancelReservation(id);
      const updatedReservationsList = reservations.map(reservation => {
        if (reservation.seats && reservation.seats.some(seat => seat.id === id)) {
          // Actualizar el estado de la reserva específica en el grupo
          return {
            ...reservation,
            seats: reservation.seats.map(seat => 
              seat.id === id ? { ...seat, status: 'cancelled' } : seat
            ),
            overallStatus: reservation.seats.every(seat => 
              seat.id === id ? true : seat.status === 'cancelled'
            ) ? 'cancelled' : reservation.overallStatus
          };
        }
        return reservation;
      });
      
      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));
      
      return updatedReservation;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const cancelGroupReservations = useCallback(async (studentId, accessCodeId) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReservations = await reservationService.cancelGroupReservations(studentId,accessCodeId);
      const updatedReservationsList = reservations.map(reservation => 
        reservation.studentId === studentId && reservation.accessCodeId === accessCodeId 
          ? { ...reservation, overallStatus: 'cancelled' } 
          : reservation
      );
      setReservations(updatedReservationsList);
      setFilteredReservations(applyFilters(updatedReservationsList, filters));
      
      return updatedReservations;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reservations, filters, applyFilters]);

  const updatePagination = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSelectedReservation = useCallback(() => {
    setSelectedReservation(null);
  }, []);

  const clearSearch = useCallback(() => {
    fetchGroupedReservations();
  }, [fetchGroupedReservations]);

  // Exportar reservas agrupadas
  const exportGroupedReservations = useCallback(async (exportFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      await reservationService.exportGroupedReservations(exportFilters);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    reservations: filteredReservations,
    allReservations: reservations,
    selectedReservation,
    loading,
    error,
    filters,
    pagination,
    fetchGroupedReservations,
    fetchAllReservations,
    searchReservations,
    applyReservationFilters,
    applyFiltersNow,
    clearFilters,
    markAsSold,
    markAsDelivered,
    markAsNoCost,
    markGroupAsSold,
    markGroupAsDelivered,
    returnReservation,
    returnGroupReservations,
    cancelReservation,
    cancelGroupReservations,
    updatePagination,
    clearError,
    clearSelectedReservation,
    clearSearch,
    exportGroupedReservations,
    setSelectedReservation,
    setReservations
  };
}; 